import { Cart } from '@commercetools/platform-sdk';
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';

import { fromAxiosNutsJson, NutsJson, webstore } from '@/api';
import { NutritionFacts } from '@/api/productNutritionFacts';
import { VolumetricSelection } from '@/lib/customizers/common';
import { sortSelectionsByProductKey } from '@/lib/customizers/mixes';

export interface CreateCustomMixResponse {
  cart?: Cart;
  cart_line?: {
    id: string;
    quantity_added: number;
  };
}

export interface MixDetails {
  asGift?: boolean;
  mixName?: string;
  quantity: number;
  selections: VolumetricSelection[];
  weight: number;
}

export async function addMixToCart(mixDetails: MixDetails) {
  const formData = new FormData();
  formData.append('quantity', `${mixDetails.quantity}`);
  if (mixDetails.mixName) formData.append('mix-name', mixDetails.mixName);
  if (mixDetails.asGift) formData.append('marked_as_gift', '1');

  const contents = sortSelectionsByProductKey(mixDetails.selections)
    .map((item: VolumetricSelection) => `${item.parts}x0${item.productKey}`)
    .join(',');
  formData.append('mix-contents', contents);
  formData.append('size', `${mixDetails.weight}`);

  const response = await fromAxiosNutsJson(
    webstore.post<NutsJson<CreateCustomMixResponse>>('/custom-mixes/create', formData),
    {},
  );

  return response;
}

export async function addCustomMixToCart(
  variantSku: string,
  variantContents: Record<string, string>,
  options: { onRedirect?: () => void } = {},
) {
  const formData = new FormData();
  formData.append('quantity', '1');
  const contents = Object.entries(variantContents)
    .map(([sku, qty]) => `${qty}x${sku}`)
    .join(',');
  formData.append('mix-contents', contents);
  formData.append('size', `${Number(variantSku.split('-')[1])}`);

  const result = await fromAxiosNutsJson(
    webstore.post<NutsJson<CreateCustomMixResponse>>('/custom-mixes/create', formData),
    options,
  );
  return result;
}

export async function getNutritionFacts(mixDetails: MixDetails) {
  const formData = new FormData();
  formData.append('weight', `${mixDetails.weight}`);

  mixDetails.selections.forEach((item: VolumetricSelection) => {
    formData.append(`data[${item.productKey}][density]`, `${item.density}`);
    formData.append(`data[${item.productKey}][parts]`, `${item.parts}`);
    formData.append(`data[${item.productKey}][price]`, `${dollars(item.volumePrice)}`);
    formData.append(`data[${item.productKey}][sku]`, `${item.productKey}`);
  });

  const data = await fromAxiosNutsJson<NutritionFacts>(
    webstore.post<NutsJson<NutritionFacts>>('/custom-mixes/item-nutrition', formData),
    {},
  );

  return data;
}
